import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Zap,
  ShieldCheck,
  Activity,
  CircleDollarSign,
  Boxes,
  type LucideIcon,
} from 'lucide-react';
import { AxiosError } from 'axios';
import { loginLocal } from '@/api/auth';
import { setStoredToken } from '@/auth/localToken';
import { useAuth, msalEnabled } from '@/auth/AuthProvider';
import { useAuthConfig } from '@/hooks/useAuthConfig';

/**
 * Login page — split-panel design.
 *
 * Left panel  : dark brand showcase. Carries the "Built and managed by the
 *               Platform Team" attribution in a deliberately quiet style —
 *               visible to anyone who lands here, but never the loudest
 *               element on the screen. Hidden below the `lg` breakpoint to
 *               keep mobile usable.
 * Right panel : the auth form proper. Microsoft SSO + email/password,
 *               surfaced based on the backend's auth config.
 *
 * The functional behaviour (login, OTP first-time flow, error mapping) is
 * unchanged from the previous version of this page. This rewrite is purely
 * visual + structural.
 */
export default function Login() {
  const navigate = useNavigate();
  const { login: msalLogin } = useAuth();
  const { data: config } = useAuthConfig();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await loginLocal(email, password);
      setStoredToken(result.access_token);
      if (result.must_change_password) {
        navigate('/change-password', { replace: true });
      } else {
        window.location.replace('/');
      }
    } catch (err) {
      const msg =
        err instanceof AxiosError
          ? (err.response?.data as { error?: { message?: string }; detail?: string })?.error?.message ??
            (err.response?.data as { detail?: string })?.detail ??
            'Login failed'
          : 'Login failed';
      setError(typeof msg === 'string' ? msg : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  const showAzure = msalEnabled && config?.azure_ad_enabled;
  const showLocal = config?.local_auth_enabled !== false;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* ─────────────────────────────────────────────────────────────
          LEFT — brand showcase (hidden below lg)
          ───────────────────────────────────────────────────────────── */}
      <BrandPanel />

      {/* ─────────────────────────────────────────────────────────────
          RIGHT — auth form
          ───────────────────────────────────────────────────────────── */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-2/5 lg:px-16 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          {/* Compact brand mark — shown on mobile (where the BrandPanel is hidden)
              and quietly on desktop so the form panel feels grounded. */}
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600">
              <Zap className="h-5 w-5 text-white" aria-hidden />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Circuitry IDP
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Internal Developer Platform
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              Welcome back
            </h2>
            <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
              Sign in to continue to the developer portal.
            </p>
          </div>

          {showAzure && (
            <button
              type="button"
              onClick={() => msalLogin()}
              className="group mb-4 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 active:scale-[0.99] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800"
            >
              <MicrosoftLogo className="h-4 w-4" />
              Sign in with Microsoft
            </button>
          )}

          {showAzure && showLocal && (
            <div className="relative my-6" aria-hidden>
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 dark:border-slate-800" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-slate-50 px-3 text-slate-400 dark:bg-slate-950 dark:text-slate-500">
                  or with email
                </span>
              </div>
            </div>
          )}

          {showLocal && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Field
                id="email"
                label="Email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={setEmail}
                placeholder="you@circuitry.ai"
              />
              <div>
                <Field
                  id="password"
                  label="Password or one-time code"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={setPassword}
                  placeholder="OTP on first login, then your password"
                />
                <p className="mt-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                  First time signing in? Use the{' '}
                  <strong className="font-medium text-slate-700 dark:text-slate-300">
                    one-time code
                  </strong>{' '}
                  your admin shared (e.g.{' '}
                  <span className="font-mono text-slate-600 dark:text-slate-400">
                    ABCD-2345-WXYZ
                  </span>
                  ) — you'll set a real password on the next screen.
                </p>
              </div>

              {error && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Sign in
              </button>
            </form>
          )}

          {/* Mobile fallback — Platform team attribution at the bottom of
              the form on small screens (where BrandPanel is hidden). */}
          <div className="mt-12 border-t border-slate-200 pt-6 text-center dark:border-slate-800 lg:hidden">
            <PlatformTeamMark variant="light" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Brand panel — left side on desktop
// ──────────────────────────────────────────────────────────────────────

function BrandPanel() {
  return (
    <div className="relative hidden w-3/5 overflow-hidden bg-slate-950 lg:flex lg:flex-col">
      {/* Layered ambient gradient. Two soft radial blooms — one indigo,
          one cyan-ish — kept low-opacity so they read as atmosphere
          rather than decoration. Pure CSS, no images. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-90"
        style={{
          background:
            'radial-gradient(60% 50% at 20% 20%, rgba(79, 70, 229, 0.25) 0%, rgba(15, 23, 42, 0) 60%), ' +
            'radial-gradient(50% 50% at 80% 75%, rgba(14, 165, 233, 0.18) 0%, rgba(15, 23, 42, 0) 65%), ' +
            'linear-gradient(160deg, #020617 0%, #0f172a 55%, #1e1b4b 100%)',
        }}
      />

      {/* Subtle dot grid — barely-there texture so the panel feels engineered */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'radial-gradient(rgba(255, 255, 255, 0.6) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />

      {/* Content overlay */}
      <div className="relative z-10 flex h-full flex-col px-12 py-12 xl:px-16">
        {/* Top — logo + name */}
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-600 shadow-lg shadow-brand-600/20">
            <Zap className="h-5 w-5 text-white" aria-hidden />
          </div>
          <div>
            <p className="text-base font-semibold text-white">Circuitry IDP</p>
            <p className="text-xs text-slate-400">Internal Developer Platform</p>
          </div>
        </div>

        {/* Middle — hero copy + value props */}
        <div className="mt-auto mb-auto max-w-md">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-white xl:text-4xl">
            Everything your team needs to ship,{' '}
            <span className="text-brand-300">in one place.</span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Deployments, environments, observability and cost analytics —
            a unified portal for the engineering org.
          </p>

          <ul className="mt-8 space-y-3">
            <FeatureRow icon={Boxes} label="Deployments & environments" />
            <FeatureRow icon={Activity} label="Observability & incident signals" />
            <FeatureRow icon={CircleDollarSign} label="Cost analytics & attribution" />
            <FeatureRow icon={ShieldCheck} label="SSO, audit logs & RBAC" />
          </ul>
        </div>

        {/* Bottom — Platform Team attribution. Quiet but always present. */}
        <div className="mt-12">
          <PlatformTeamMark variant="dark" />
        </div>
      </div>
    </div>
  );
}

function FeatureRow({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}) {
  return (
    <li className="flex items-center gap-3 text-sm text-slate-300">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/5 ring-1 ring-white/10">
        <Icon className="h-3.5 w-3.5 text-slate-300" />
      </span>
      {label}
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Platform team attribution — visible but never the loudest element
// ──────────────────────────────────────────────────────────────────────
//
// Two variants:
//   - dark : used inside the BrandPanel (dark slate background)
//   - light: used at the bottom of the form on mobile
//
// Deliberate styling choices:
//   * No bg-fill, no ring glow — just a small icon + text
//   * slate-400 / slate-500 — readable but never grabs attention
//   * `font-medium`, not `font-bold` — present, not shouting
//   * Hover gently lifts color so it feels alive without animating
//
// The product owner explicitly asked: "not very bright color, but by
// seeing it someone easily notices." This is the calibration.

function PlatformTeamMark({ variant }: { variant: 'dark' | 'light' }) {
  const dark = variant === 'dark';
  return (
    <div
      className={`group inline-flex items-center gap-2.5 ${
        dark ? '' : 'mx-auto'
      }`}
    >
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-md ring-1 transition ${
          dark
            ? 'bg-white/5 ring-white/10 group-hover:bg-white/10'
            : 'bg-slate-100 ring-slate-200 group-hover:bg-slate-200 dark:bg-slate-800 dark:ring-slate-700 dark:group-hover:bg-slate-700'
        }`}
      >
        <Zap
          className={`h-3 w-3 transition ${
            dark
              ? 'text-slate-300 group-hover:text-slate-100'
              : 'text-slate-500 group-hover:text-slate-700 dark:text-slate-400 dark:group-hover:text-slate-300'
          }`}
          aria-hidden
        />
      </span>
      <div className="text-left leading-tight">
        <p
          className={`text-[10px] uppercase tracking-[0.18em] ${
            dark ? 'text-slate-500' : 'text-slate-400 dark:text-slate-500'
          }`}
        >
          Built & managed by
        </p>
        <p
          className={`text-xs font-medium transition ${
            dark
              ? 'text-slate-300 group-hover:text-slate-100'
              : 'text-slate-600 group-hover:text-slate-800 dark:text-slate-400 dark:group-hover:text-slate-200'
          }`}
        >
          Circuitry Platform Team
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Field — single labelled input. Extracted so the email + password
// boxes share consistent styling without duplicating every Tailwind class.
// ──────────────────────────────────────────────────────────────────────

function Field({
  id,
  label,
  type,
  autoComplete,
  required,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  type: string;
  autoComplete: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-brand-500 dark:focus:ring-brand-500/30"
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Microsoft logo — inline SVG so we don't pull in an icon dependency
// just for one button.
// ──────────────────────────────────────────────────────────────────────

function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 21 21"
      className={className}
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
